import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { Component, ElementRef, HostBinding, Inject, NgZone, PLATFORM_ID, effect } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { PageDataResponse } from '@core-mkt/resolvers/page-data/page-data-response';
import { CountdownService } from '@core-mkt/services/countdown/countdown.service';
import { StickyHeaderService } from '@core-mkt/services/sticky-header/sticky-header.service';
import { DurationTextPipe } from '@core-mkt/services/duration/duration-text.pipe';
import { cloneDeep } from 'lodash-es';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import type { Banner, EntryData, HeaderData } from './banner';
import { mockRoute } from './banner-mock-data';

@Component({
  selector: 'banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
  standalone: true,
  imports: [CommonModule, DurationTextPipe],
})
export class BannerComponent implements OnInit, AfterViewInit, OnDestroy {
  resolverData: PageDataResponse;
  pageData: Banner = null;
  headerData: HeaderData;
  entryData: EntryData;
  isPromoTime = false;
  isAfterPromoTime = false;
  countdown: Observable<number>;
  offerEndTime: number = null;
  offerStartTime: number = null;
  offerResetTime: number = null;
  displayRE = false; // legacy flag name preserved for tests
  daysRemaining = false;
  // Use browser-safe timeout handle type (works whether DOM or Node typings are present)
  promoTimeOutReference: ReturnType<typeof setTimeout> | null = null;
  resetTimeOutReference: ReturnType<typeof setTimeout> | null = null;
  // EPS-4465: handle for the next-tick re-measure scheduled by the sticky effect,
  // so repeat signal changes replace the pending pass instead of stacking, and a
  // teardown mid-flight cannot write to a detached host.
  republishHeightTimeout: ReturnType<typeof setTimeout> | null = null;
  timeToPromoStart: number = null;
  timeToResetStart: number = null;
  private destroy$ = new Subject<void>();

  private isBrowser: boolean;
  private resizeObserver?: ResizeObserver;

  /**
   * EPS-4439: when a header on this page pins itself to the top of the
   * viewport, lift the topBanner into `position: sticky; top: 0` so it stays
   * locked above that header instead of scrolling out of view with the rest of
   * the page flow. Paired with the CSS var published below, which shifts the
   * sticky header down by the banner height so the two stack without overlap.
   *
   * EPS-4465: `isNavSticky` is not the whole answer. DEC's simplified nav is
   * unconditionally `position: sticky` in its own CSS (CRO-1093 / EPS-4220)
   * while DEC ships `isNavSticky: false`, so on every DEC page the banner
   * stayed in flow and slid up behind the pinned nav. StickyHeaderService lets
   * a header that pins for its own reasons say so; the Craft flag still covers
   * the brands that pin through the global `.sticky-header` class.
   */
  @HostBinding('class.sticky-banner-active') get isStickyBannerActive(): boolean {
    const headerPins = this.headerData?.isNavSticky || this.stickyHeaderService.isSticky();
    return !!(headerPins && this.displayPromo && !this.hideStickyBanner);
  }

  constructor(
    private route: ActivatedRoute,
    private countdownService: CountdownService,
    private ngZone: NgZone,
    private elementRef: ElementRef<HTMLElement>,
    private stickyHeaderService: StickyHeaderService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    // The header module resolves after this one, so the sticky declaration can
    // arrive (or change on navigation) after the banner has already measured
    // itself. Re-measure on the next tick, once the host class has landed and
    // the pb-1 skirt is part of the box, so --countdown-banner-height matches
    // what the header is about to offset itself by.
    effect(() => {
      this.stickyHeaderService.isSticky();
      if (!this.isBrowser) return;
      if (this.republishHeightTimeout) clearTimeout(this.republishHeightTimeout);
      this.republishHeightTimeout = setTimeout(() => {
        this.republishHeightTimeout = null;
        this.publishBannerHeight();
      });
    });
    if (!this.route.snapshot) {
      this.resolverData = mockRoute.snapshot.data.pageData;
    } else {
      this.resolverData = cloneDeep(this.route.snapshot.data.pageData) as PageDataResponse;
    }
    if (this.resolverData && this.resolverData.craftData?.header) {
      this.pageData = this.resolverData.craftData.header.aceHeaderPromoBanner[0] as Banner;
    }
    if (this.resolverData && this.resolverData.craftData.headerData) {
      this.headerData = this.resolverData.craftData.headerData;
    }
    if (
      this.resolverData &&
      this.resolverData.craftData.banners &&
      this.resolverData.craftData.banners.topBanner.length > 0
    ) {
      this.pageData = this.resolverData.craftData.banners.topBanner[0];
    }
    // Banner coupon is read directly from route data by CouponAggregationService
    // No need to set it here - avoids timing issues and duplicate API calls
  }

  get afterMainPromo(): boolean {
    return this.isAfterPromoTime;
  }

  get promotionText(): string {
    if (this.isAfterPromoTime && this.pageData?.promotionReset && this.pageData.promotionReset.length > 0) {
      const resetText = this.pageData.promotionReset[0].resetText;
      if (resetText) {
        return resetText;
      }
    }
    return this.pageData?.promotionText || '';
  }

  get displayPromo(): boolean {
    if (!this.pageData || !this.pageData.showHidePromoBanner) {
      return false;
    }

    // Only show banner if we have a promotion start date, and we are past that time
    if (this.pageData.promotionStartDateTime) {
      return this.isPromoTime;
    }

    // If no start time is set, show the banner (maintain existing behavior)
    return true;
  }

  get startTime(): string {
    if (this.pageData.promotionStartDateTime === null) {
      return '';
    }
    return this.pageData.promotionStartDateTime;
  }

  get endTime(): string {
    return this.isAfterPromoTime
      ? this.pageData.promotionReset[0].promotionEndsDateTime
      : this.pageData.promotionEndsDateTime;
  }

  get hideStickyBanner(): boolean {
    if (this.headerData) {
      return this.headerData.hideStickyBanner;
    }
    return this.entryData?.hideStickyBanner ?? false;
  }

  ngOnInit(): void {
    if (this.pageData === null) {
      return;
    }

    // Compute initial timing state (no timers scheduled yet; safe for SSR)
    this.setTimerValues();
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return; // Skip scheduling and DOM writes on server
    if (this.pageData !== null) this.scheduleClientTimers();
    // EPS-4439: publish the host's rendered height as a CSS custom property
    // on <html> so the global .sticky-header rule can `top: var(--countdown-banner-height)`
    // and sit directly below at every scroll depth. Always publish the initial
    // height (even without ResizeObserver) so the fixed header is positioned
    // correctly on first paint.
    this.publishBannerHeight();
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.publishBannerHeight());
      this.resizeObserver.observe(this.elementRef.nativeElement);
    }
  }

  private publishBannerHeight(): void {
    if (!this.isBrowser) return;
    // EPS-4465: measure with getBoundingClientRect(), not offsetHeight.
    // offsetHeight rounds to the NEAREST integer, so a banner that actually
    // renders at e.g. 43.6px publishes 44px, the sticky header offsets itself
    // to top: 44px, and a ~0.4px seam opens between the banner's bottom edge
    // and the header — page content scrolling underneath leaks through it, most
    // visibly behind the dark timer chips that overhang into the skirt.
    // Math.floor on the fractional height biases the other way: the header
    // tucks up under the banner by at most 1px, and the only thing it covers is
    // the white pb-1 skirt, which is the same colour as the header itself.
    const height = this.isStickyBannerActive
      ? Math.floor(this.elementRef.nativeElement.getBoundingClientRect().height)
      : 0;
    this.document.documentElement.style.setProperty('--countdown-banner-height', `${height}px`);
  }

  ngOnDestroy(): void {
    if (this.promoTimeOutReference) {
      clearTimeout(this.promoTimeOutReference);
      this.promoTimeOutReference = null;
    }
    if (this.resetTimeOutReference) {
      clearTimeout(this.resetTimeOutReference);
      this.resetTimeOutReference = null;
    }
    if (this.republishHeightTimeout) {
      clearTimeout(this.republishHeightTimeout);
      this.republishHeightTimeout = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
    if (this.isBrowser) {
      this.document.documentElement.style.removeProperty('--countdown-banner-height');
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private activatePromoTimeout(): void {
    if (!this.isBrowser || !this.timeToPromoStart || this.timeToPromoStart <= 0) return;
    this.ngZone.runOutsideAngular(() => {
      this.promoTimeOutReference = setTimeout(() => {
        this.ngZone.run(() => (this.isPromoTime = true));
      }, this.timeToPromoStart);
    });
  }

  private activateResetTimeout(): void {
    if (!this.isBrowser || !this.timeToResetStart || this.timeToResetStart <= 0) return;
    this.ngZone.runOutsideAngular(() => {
      this.resetTimeOutReference = setTimeout(() => {
        this.ngZone.run(() => (this.isAfterPromoTime = true));
      }, this.timeToResetStart);
    });
  }

  private scheduleClientTimers(): void {
    // Only schedule if transitions still in future
    if (this.timeToPromoStart && this.timeToPromoStart > 0) this.activatePromoTimeout();
    if (this.timeToResetStart && this.timeToResetStart > 0) this.activateResetTimeout();
  }

  private setTimerValues(): void {
    const dayInMilli = 86400000;
    const min5InMilli = 300000;
    const now = Date.now();
    this.offerStartTime = this.getOfferStartTime();
    this.offerEndTime = this.getOfferEndTime();
    // reset promo phase derived values
    this.timeToPromoStart = null;
    this.offerResetTime = null;
    this.timeToResetStart = null;
    this.isAfterPromoTime = false;
    // Handle reset phase logic
    if (this.pageData?.promotionReset && this.pageData.promotionReset.length > 0) {
      const resetData = this.pageData.promotionReset[0];
      const resetDateTime = resetData.promotionEndsDateTime;

      // Set reset end time if provided
      if (resetDateTime) {
        this.offerResetTime = new Date(resetDateTime).valueOf();
        if (!isNaN(this.offerResetTime)) {
          this.timeToResetStart = this.offerResetTime - now;

          // If reset end time has passed, we're in the after-promo state
          if (this.timeToResetStart <= 0) {
            this.isAfterPromoTime = true;
          }
          // If we're past main promo end and have reset data, we're also in reset phase
          else if (
            this.offerEndTime &&
            !isNaN(this.offerEndTime) &&
            now > this.offerEndTime &&
            (resetData.resetText || resetDateTime)
          ) {
            this.isAfterPromoTime = true;
          }
        }
      }
    }
    // missing start or end -> displayRE flag
    if (!this.startTime || this.startTime.length === 0 || !this.endTime) {
      this.displayRE = true;
    } else {
      this.displayRE = false;
    }

    // determine promo activity relative to start/end
    if (this.offerStartTime != null) {
      const msUntilStart = this.offerStartTime - now; // future if >0
      if (msUntilStart <= 0) {
        // already started
        this.isPromoTime = true;
      } else if (msUntilStart < min5InMilli) {
        // within 5 minutes window leading up to start
        this.isPromoTime = true;
      } else if (msUntilStart > dayInMilli) {
        this.timeToPromoStart = msUntilStart;
        this.isPromoTime = false;
      } else {
        this.isPromoTime = false;
      }
    } else {
      // no start date means show banner (handled by getter), no need to set isPromoTime
      this.isPromoTime = false;
    }

    // Turn off promo if past end (and before reset takeover) or past reset window
    if (
      (this.offerEndTime != null && this.offerEndTime < now && !this.offerResetTime) ||
      (this.offerResetTime && this.offerResetTime < now)
    ) {
      this.isPromoTime = false;
    }

    // countdown target selection
    this.countdown = this.countdownService.create(
      this.isAfterPromoTime ? this.offerResetTime : this.offerEndTime,
      this.offerStartTime,
    );
    this.countdown.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data > 0) {
        const milli = data;
        const hours = milli / 1000 / 60 / 60;
        this.daysRemaining = hours > 24;
      }
    });

    // Communicate current promo display status to CountdownService
    this.countdownService.setSaleValue(this.displayPromo);
  }

  private getOfferEndTime = (): number => {
    let endTime: number = null;
    if (this.pageData) {
      const endDate = new Date(this.pageData.promotionEndsDateTime);
      endTime = endDate.valueOf();
    }
    return endTime;
  };

  private getOfferStartTime = (): number => {
    if (!this.pageData || this.pageData.promotionStartDateTime == null) {
      return null;
    }
    const startDate = new Date(this.pageData.promotionStartDateTime);
    const ms = startDate.valueOf();
    if (isNaN(ms)) return null;
    return ms;
  };
}
